import type * as React from "react";
import { memo, type ReactNode, useCallback } from "react";
import CustomNumberFormat, { type NumberFormatEvent } from "../../../../../common/components/CustomNumberFormat";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";

interface SingleTechFilterProps {
  className?: string;
  title: ReactNode; // Can contain html!
  isInvalid?: boolean;
  min: number | undefined;
  max: number | undefined;
  onUpdateValues: (type: "min" | "max", value: number | undefined) => void;
  placeholderSuffix?: string;
  "data-test-id"?: string;
}

const SingleTechFilter: React.FC<SingleTechFilterProps> = (props) => {
  const { title, isInvalid, min, max, onUpdateValues, placeholderSuffix } = props;
  const { t } = useTranslation();
  const updateMinValue = useCallback((e: NumberFormatEvent) => onUpdateValues("min", e.floatValue), [onUpdateValues]);
  const updateMaxValue = useCallback((e: NumberFormatEvent) => onUpdateValues("max", e.floatValue), [onUpdateValues]);

  const minLabel = t("searchPage.refinement.value.label.min");
  const maxLabel = t("searchPage.refinement.value.label.max");
  const placeholderSuffixLabel = placeholderSuffix && t(placeholderSuffix);

  return (
    <Stack spacing={2}>
      <Typography>{title}</Typography>
      <Stack spacing={3} direction={"row"}>
        <CustomNumberFormat
          data-test-id={props["data-test-id"] ? `tech-filter-${props["data-test-id"]}-min` : undefined}
          error={isInvalid}
          value={min ?? null}
          label={placeholderSuffixLabel ? `${minLabel} ${placeholderSuffixLabel}` : minLabel}
          handleState={updateMinValue}
          helperText={isInvalid ? t("searchPage.refinement.value.minMaxError") : undefined}
          size={"small"}
        />
        <CustomNumberFormat
          data-test-id={props["data-test-id"] ? `tech-filter-${props["data-test-id"]}-max` : undefined}
          value={max ?? null}
          label={placeholderSuffixLabel ? `${maxLabel} ${placeholderSuffixLabel}` : maxLabel}
          handleState={updateMaxValue}
          size={"small"}
        />
      </Stack>
    </Stack>
  );
};

export default memo(SingleTechFilter);
